<script setup lang="ts">
import { OdsCarousel, OdsCarouselSlide } from "@ods/lidl-web-vuejs";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";

import type { GridBox } from "@reco/shared/src/types/gridbox";
import type { Recommendation } from "@reco/shared/src/types/recommendation";

import { TrackingEventType } from "@reco/service/src/constants";
import Tracking from "@reco/service/src/Tracking";
import { getGridboxType } from "@reco/service/src/utils/helpers";
import { PageType } from "@reco/service/src/utils/pageType";
import RecoHeading from "@reco/shared/src/components/RecoHeading.vue";
import { ColorScheme, Scenario } from "@reco/shared/src/constants";
import GridboxFragment from "@reco/widgets/src/components//GridboxFragment.vue";
import { useHeading } from "@reco/widgets/src/composables/heading";
import { useShouldRender } from "@reco/widgets/src/composables/shouldRender";
import { useSubheading } from "@reco/widgets/src/composables/subheading";
import { useShopAsstEntryPoint } from "@reco/widgets/src/composables/useShopAsstEntryPoint.ts";
import { useShopAsstTracking } from "@reco/widgets/src/composables/useShopAsstTracking.ts";
import { usePageStore } from "@reco/widgets/src/stores/pageStore";
import { getSliderItemSize } from "@reco/widgets/src/utils";
import Overlay from "./DiscoverOverlay.vue";

export type SliderProps = {
  heading: string;
  subHeading: string;
  headingElement?: string;
  showTitle?: boolean;
  colorScheme: ColorScheme;
  recommendations: Recommendation[];
  creativeSlot?: string;
  creativeSlotReco?: string;
  scenario: Scenario;
  brand?: string;
  category?: string;
  categoryName?: string;
  showEntryPoint?: boolean;
};

const props = defineProps<SliderProps>();
const { t } = useI18n();
const { country, pageType } = usePageStore();
const finalHeading = useHeading(() => ({
  scenario: props.scenario,
  headingProp: props.heading,
  brand: props.brand,
  category: props.category,
  categoryName: props.categoryName,
  showEntryPoint: props.showEntryPoint,
}));
const finalSubHeading = useSubheading(() => ({ scenario: props.scenario, subheadingProp: props.subHeading }));

const isSponsored = computed(() => props.scenario === Scenario.SPONSORED && pageType === PageType.PDP);
const { shouldRenderWidget, shouldRenderTitle } = useShouldRender(props, finalHeading, "Slider", isSponsored);
const hasCTA = computed(() => {
  if (props.showEntryPoint !== undefined) return props.showEntryPoint;
  return props.scenario === Scenario.PRODUCTS && pageType === PageType.PDP;
});
const itemSize = computed(() => getSliderItemSize(props.recommendations[0]));
const rdpUrl = computed(() => globalThis.location.pathname?.replace("/p/", "/r/") ?? "");

const showRdpOverlay = (index: number): boolean => {
  const isLastItem = index === props.recommendations.length - 1;

  if (props.showEntryPoint !== undefined) return props.showEntryPoint && isLastItem;

  const isProductScenario = props.scenario === Scenario.PRODUCTS;
  const isPdp = globalThis.location.pathname && pageType === PageType.PDP;
  const isStorybook = import.meta.env.STORYBOOK;

  return !!(isProductScenario && (isPdp || isStorybook) && isLastItem);
};

const { showShopAsstEntryPoint, shopAsstContext } = useShopAsstEntryPoint(props);
const { onShopAsstCtaClick } = useShopAsstTracking({
  recommendations: props.recommendations,
  creativeSlot: props.creativeSlot,
  gridboxType: getGridboxType(country, props.recommendations[0]?.list),
});

onMounted(() => {
  if (props.scenario === Scenario.SPONSORED && props.recommendations.length > 0) {
    Tracking.push(
      {
        pageType,
        recommendations: props.recommendations as unknown as GridBox[],
        extraParams: {
          adPlacementLoadBeacon: props.recommendations[0]?.adPlacementLoadBeacon,
        },
      },
      TrackingEventType.SponsoredRecommendationLoad,
    );
  }
});
</script>

<template>
  <div v-if="shouldRenderWidget" ref="root" class="reco-slider">
    <RecoHeading
      v-if="shouldRenderTitle"
      :sub-heading="finalSubHeading"
      :heading-element="headingElement"
      :color-scheme="colorScheme"
    >
      {{ finalHeading }}
    </RecoHeading>
    <div v-if="hasCTA" class="reco-slider__cta-wrap">
      <a class="reco-slider__cta-link" :title="$t('reco-frontend-next.Slider.more_recommendations')" :href="rdpUrl">
        <svg enable-background="new 0 0 60 60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
          <path
            d="m57.7 28.6-8.3-8.3c-.2-.2-.5-.2-.7 0-1 1-1 2.6 0 3.5l4.7 4.7h-49.9c-.3 
                    0-.5.2-.5.5 0 1.4 1.1 2.5 2.5 2.5h47.9l-4.7 4.7c-1 1-1 2.6 0 3.5.2.2.5.2.7 
                    0l8.3-8.3c.8-.8.8-2 0-2.8z"
            fill="#0050AA"
          />
        </svg>
        {{ $t("reco-frontend-next.Slider.more_recommendations") }}
      </a>
    </div>
    <div class="reco-slider__carousel-wrap">
      <nav>
        <OdsCarousel
          :show-scroll-indicator="false"
          :gutter="0"
          :full-width="true"
          :title="heading"
          :item-size="itemSize"
          :previous-slide-left="t('reco-frontend-next.Slider.scroll_left')"
          :next-slide-right="t('reco-frontend-next.Slider.scroll_right')"
          layout="flexbox"
          aria-live="none"
        >
          <OdsCarouselSlide v-for="(item, idx) in recommendations" :key="idx" :aria-label="undefined">
            <div
              :class="[
                'reco-slider__fragment-wrap',
                `reco-slider__fragment-wrap--${getGridboxType(country, item.list)}`,
              ]"
            >
              <sa-entry-point
                v-if="showShopAsstEntryPoint(idx)"
                ref="shopping-assistant"
                variant="reco"
                :context="shopAsstContext"
                @cta-button-click="onShopAsstCtaClick"
              />
              <template v-else>
                <GridboxFragment
                  :product="item"
                  :creative-slot="props.creativeSlot"
                  :creative-slot-reco="props.creativeSlotReco"
                />
                <Overlay
                  v-if="showRdpOverlay(idx)"
                  :cta-text="$t('reco-frontend-next.Slider.more_recommendations')"
                  :cta-href="rdpUrl"
                />
              </template>
            </div>
          </OdsCarouselSlide>
        </OdsCarousel>
      </nav>
    </div>
    <div v-if="hasCTA && !isSponsored" class="reco-slider__footer">
      <span class="reco-slider__footerline"></span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.reco-slider {
  &__cta-wrap {
    display: flex;
    justify-content: flex-end;
    margin: -1rem 0.25rem var(--ods-space-4);
  }

  &__cta-link {
    color: var(--ods-color-text-surface-interaction-link-default);
    text-decoration: underline;
    font-size: 1rem;
    line-height: 1.4;
    font-weight: 400;

    @media (min-width: $ods-breakpoint-desktop-min) {
      font-size: 1.125rem;
    }

    display: flex;
    cursor: pointer;

    svg {
      width: 25px;
      height: 25px;
      -webkit-transform: translateX(-7px);
      -ms-transform: translateX(-7px);
      transform: translateX(-7px);
    }

    &:focus-visible {
      outline: 1px solid var(--ods-color-border-interaction-primary-default);
    }
  }

  &__carousel-wrap {
    position: relative;
  }

  &__fragment-wrap {
    height: 100%;
    display: grid;
    grid-template-rows: 1.5rem 1fr;
    position: relative;

    &--minimal {
      grid-template-rows: 0;
    }
  }

  &__footer {
    width: calc(100% - 0.5rem);
    overflow: hidden;
    display: flex;
    border-color: var(--ods-color-border-divider);
    margin: 1rem 0.25rem;
  }

  &__footerline {
    border-bottom: 2px solid;
    flex-grow: 1;
    position: relative;
    bottom: 0.375rem;
    margin-right: 0.5rem;
    border-color: inherit;
  }

  sa-entry-point {
    display: block;
    grid-row: 1 / -1;
    width: 100%;
    height: 100%;

    & > :first-child {
      height: 100%;
    }
  }
}
</style>
